/* ===================================================================
   Reviews inbox - EXACT port of the Reviews screen from Steve's Claude
   Design prototype (design/claude_design_handoff_2026-07-03/
   Rhody Reviews.dc.html: desktop 671-838, mobile 2103-2231; runtime
   decorate()/_toneText() 3256-3305). Corbato, 2026-07-03.

   ONE responsive component (see styles/reviews.css):
     - desktop  >=1024px : list (1.55fr) + right rail (1fr)
     - mobile   <768px   : full-list stack, colored-left-border cards
     - enhanced 768-1023 : mobile IA, roomier

   PRESENTATION-ONLY reskin. The data layer is UNCHANGED from the prior
   build: useReviews() (GET /api/v1/reviews) feeds the list; each row's
   pending AI draft (draft_response + draft_id) folds in inline; the
   Approve / Edit / Skip / bulk / CSV handlers still call the same outbox
   endpoints via useOutboxActions(). Segment + filter COUNTS derive from
   the live list. A review with reply_text is "Replied"; otherwise "Needs
   reply". Google-only as a review source (2026-06-28).

   HARD PRODUCT RULE - REPLIES NEVER AUTO-POST:
   The ONLY things that post a reply are the operator clicking "Approve &
   post" on a card, or the explicit bulk "Approve all five-star drafts".
   Tone chips (Warm / Short / Sincere) only reswap the DRAFT text locally;
   they never post. There is no auto-send path anywhere.
   =================================================================== */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Icon, SearchPath, CheckPath, PlusPath } from '../components/ui';
import type { ReviewItem } from '../components/ui';
import { useReviews, useOutboxActions, useSessionToken } from '../hooks';
import { toReviewItem, friendlyError } from '../lib/display';
import { useAdminView } from '../admin/adminViewContext';
import '../styles/reviews.css';

type Tone = 'warm' | 'short' | 'sincere';

/* Small stroke/fill glyphs used inline on this screen (kept local so the
   port matches the prototype's exact SVGs without touching shared ui.tsx). */
function Stars15({ n }: { n: number }) {
  return (
    <span className="rv-stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          className="rv-star"
          viewBox="0 0 24 24"
          fill={i < n ? '#fbbc04' : '#e0e3e7'}
        >
          <path d="M12 2l3 6.9 7.5.6-5.7 4.9 1.8 7.3L12 17.8 5.4 21.7l1.8-7.3L1.5 9.5 9 8.9z" />
        </svg>
      ))}
    </span>
  );
}
const GoogleG = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="#4285F4">
    <path d="M12 11v2.6h4.3c-.2 1.1-1.4 3.2-4.3 3.2a4.8 4.8 0 0 1 0-9.6c1.5 0 2.5.6 3 1.1l2-2A7.5 7.5 0 1 0 12 19.5c4.3 0 7.2-3 7.2-7.3 0-.5 0-.9-.1-1.2z" />
  </svg>
);
const SparkGlyph = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="#1a73e8">
    <path d="M12 2l2 6 6 .5-4.6 4 1.4 6L12 15.8 7.2 18.5l1.4-6L4 8.5 10 8z" />
  </svg>
);
const ReplyArrow = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2">
    <path d="M9 17l-5-5 5-5M4 12h11a5 5 0 0 1 5 5v1" />
  </svg>
);

/* Sentiment mapping, verbatim from the prototype decorate() (3263-3268),
   keyed off star rating + whether the review is flagged for care. */
function sentimentOf(stars: number, flagged: boolean) {
  const s5 = stars >= 5;
  const s34 = stars === 3 || stars === 4;
  const accent = flagged ? '#d93025' : s5 ? '#34a853' : s34 ? '#fbbc04' : '#d93025';
  const label = flagged ? 'Needs care' : s5 ? 'Positive' : s34 ? 'Mixed' : 'Unhappy';
  const bg = flagged ? '#fce8e6' : s5 ? '#e6f4ea' : s34 ? '#fef7e0' : '#fce8e6';
  const fg = flagged ? '#c5221f' : s5 ? '#188038' : s34 ? '#b06000' : '#c5221f';
  return { accent, label, bg, fg };
}

/* Tone-swapped draft text, verbatim from the prototype _toneText() (3089-3102).
   Warm = the original AI draft; Short / Sincere are name-personalized rewrites
   that differ for a flagged (low-star) vs. positive review. */
function toneText(item: ReviewItem, tone: Tone, flagged: boolean): string {
  const first = (item.who || '').split(' ')[0];
  if (tone === 'short') {
    return flagged
      ? "Thanks for the honest feedback, " + first + ". I'll reach out today to make this right."
      : 'Thank you, ' + first + '! We really appreciate it.';
  }
  if (tone === 'sincere') {
    return flagged
      ? first +
          ", I'm truly sorry we fell short. That's not the standard we hold ourselves to. I'd like to make it right personally and will call you today."
      : 'Thank you so much, ' +
          first +
          '. Reviews like yours are exactly why we do what we do. We are grateful for your trust and cannot wait to help again.';
  }
  return item.draft ?? '';
}

export function Reviews() {
  // OPTION A: an operator viewing a client's inbox is strictly READ-ONLY.
  // Drafts are visible; every write action is hidden and the hook is inert.
  const readOnly = useAdminView() !== null;

  const navigate = useNavigate();

  // Filter state persisted to URL params (?seg / ?q). Segment keyed by LABEL.
  const [searchParams, setSearchParams] = useSearchParams();
  const [seg, setSeg] = useState(0);
  const [searchText, setSearchText] = useState(() => searchParams.get('q') ?? '');

  // Per-review local UI state (mirrors the prototype's replyTone / replyDrafts
  // / editingId). Tone reswaps the shown draft; editing opens an inline
  // textarea; an edited draft overrides the tone text until a tone is re-picked.
  const [toneById, setToneById] = useState<Record<string, Tone>>({});
  const [draftById, setDraftById] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  // Transient success/summary toast (U7 bulk approve, U9 export feedback).
  const [toast, setToast] = useState<string | null>(null);
  // Inline error banner for a single approve/skip that failed. Kept separate
  // from the bulk-approve summary toast (which reports its own failures).
  const [actionErr, setActionErr] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  function syncUrl(next: { segLabel?: string; q?: string }) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        const set = (k: string, v: string | undefined, dflt: string) => {
          if (v && v !== dflt) p.set(k, v);
          else p.delete(k);
        };
        if ('segLabel' in next) set('seg', next.segLabel, 'Needs reply');
        if ('q' in next) set('q', next.q?.trim() ? next.q : undefined, '');
        return p;
      },
      { replace: true },
    );
  }

  // Bridge the Supabase login to a data-plane rr_ token (deduped globally).
  useSessionToken();

  // ---- LIVE reviews + outbox actions ----
  const { data, isLoading, isError, error } = useReviews({ limit: 500 });
  const {
    approve,
    edit,
    skip,
    writeReply,
    editReply,
    isPending: actionPending,
  } = useOutboxActions();

  // U6: manual "Write a reply" composer state, keyed per review. `text` is the
  // in-progress reply; `busy` guards double-submit; `err` shows an inline,
  // plain-language error (esp. the 409 draft_exists -> "an AI draft exists").
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [replyBusy, setReplyBusy] = useState<string | null>(null);
  const [replyErr, setReplyErr] = useState<Record<string, string>>({});

  // FIX 1: "Edit reply" on an ALREADY-REPLIED review. `editReplyId` is the review
  // currently open for editing; `editReplyText` holds the in-progress edit;
  // `editReplyBusy` guards double-submit; `editReplyErr` shows an inline error.
  const [editReplyId, setEditReplyId] = useState<string | null>(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [editReplyBusy, setEditReplyBusy] = useState(false);
  const [editReplyErr, setEditReplyErr] = useState<string | null>(null);

  // Map live rows -> ReviewItem, and split by reply state.
  const allItems = useMemo<ReviewItem[]>(
    () => (data?.reviews ?? []).map((r) => toReviewItem(r)),
    [data],
  );

  // review_id -> outbox draft_id, so the card actions (keyed by review id)
  // act on the right pending draft. Only unanswered reviews with a pending
  // AI draft appear here.
  const draftIdByReview = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of data?.reviews ?? []) {
      if (r.draft_id) m.set(r.review_id, r.draft_id);
    }
    return m;
  }, [data]);
  const repliedIds = useMemo(
    () => new Set((data?.reviews ?? []).filter((r) => r.reply_text).map((r) => r.review_id)),
    [data],
  );

  // status buckets, matching the prototype's needs / replied / flagged / all.
  // "flagged / needs care" = escalated OR a low-star (<=2) review; the
  // prototype flags low-star + escalated reviews for a personal reply.
  const isReplied = (i: ReviewItem) => repliedIds.has(i.id);
  const isFlagged = (i: ReviewItem) => Boolean(i.escalated) || i.stars <= 2;
  const needsReply = useMemo(() => allItems.filter((i) => !isReplied(i)), [allItems, repliedIds]);
  const replied = useMemo(() => allItems.filter((i) => isReplied(i)), [allItems, repliedIds]);
  const flaggedItems = useMemo(() => allItems.filter(isFlagged), [allItems]);

  const needsCount = needsReply.length;
  const repliedCount = replied.length;
  const flaggedCount = useMemo(
    () => needsReply.filter(isFlagged).length,
    [needsReply],
  );
  const allCount = allItems.length;
  const fiveStarNeeds = useMemo(
    () => needsReply.filter((i) => i.stars >= 5).length,
    [needsReply],
  );

  const TABS = [
    { label: 'Needs reply', badge: needsCount, items: needsReply },
    { label: 'Replied', badge: repliedCount, items: replied },
    { label: 'Flagged', badge: flaggedCount, items: flaggedItems },
    { label: 'All', badge: allCount, items: allItems },
  ];
  const segItems = TABS[seg]?.items ?? needsReply;

  // Resolve URL ?seg=<label> -> index (default Needs reply / 0).
  const segLabels = TABS.map((t) => t.label).join('|');
  useEffect(() => {
    const wanted = searchParams.get('seg');
    if (!wanted) return;
    const idx = TABS.findIndex((t) => t.label === wanted);
    if (idx >= 0 && idx !== seg) setSeg(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segLabels, searchParams]);

  // Search filter (name or text, case-insensitive). Google-only source, so no
  // platform dropdown here (deep links elsewhere are not ingested).
  const visible = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return segItems;
    return segItems.filter((r) => `${r.who} ${r.text}`.toLowerCase().includes(q));
  }, [segItems, searchText]);

  // ---- per-review UI helpers ----
  function effectiveDraft(item: ReviewItem): string {
    if (draftById[item.id] != null) return draftById[item.id];
    const tone = toneById[item.id] ?? 'warm';
    return toneText(item, tone, isFlagged(item));
  }
  function setTone(id: string, t: Tone) {
    setToneById((s) => ({ ...s, [id]: t }));
    // Picking a tone discards any manual edit (prototype: delete replyDrafts[id]).
    setDraftById((s) => {
      const n = { ...s };
      delete n[id];
      return n;
    });
  }
  function toggleEdit(item: ReviewItem) {
    setEditingId((cur) => {
      if (cur === item.id) return null; // "Done"
      // opening: seed the textarea with the current effective draft
      setDraftById((s) => (s[item.id] != null ? s : { ...s, [item.id]: effectiveDraft(item) }));
      return item.id;
    });
  }

  // ---- action handlers wired to the live outbox endpoints ----
  // Each fires ONLY on an explicit operator click. Replies never auto-post.
  function handleApprove(item: ReviewItem) {
    const draftId = draftIdByReview.get(item.id);
    if (!draftId) return; // no pending draft (already replied / none)
    const shown = item.draft ?? '';
    const next = effectiveDraft(item).trim();
    setActionErr(null);
    // If the operator changed the text (tone swap or manual edit), save the
    // edit; otherwise approve as-drafted. Either way the backend routes through
    // the human approval gate before recording it posted.
    const op = next && next !== shown.trim() ? edit(draftId, next) : approve(draftId);
    setEditingId((cur) => (cur === item.id ? null : cur));
    // The rejection is the outbox mutation's error. Surface it in the inline
    // banner (was silently swallowed with a false "surfaced elsewhere" note).
    void op.catch((e) => {
      setActionErr(
        friendlyError((e as Error)?.message, 'We could not approve that reply just now. Please try again.'),
      );
    });
  }
  function handleSkip(id: string) {
    const draftId = draftIdByReview.get(id);
    if (!draftId) return;
    setActionErr(null);
    void skip(draftId).catch((e) => {
      setActionErr(
        friendlyError((e as Error)?.message, 'We could not skip that draft just now. Please try again.'),
      );
    });
  }
  // U6: post a MANUAL reply to a review that has no AI draft. Fires only on the
  // owner's explicit click. Maps the backend error shapes to plain sentences;
  // a 409 draft_exists means an AI draft appeared for this review -- point the
  // owner at it (a refresh surfaces the AI-draft approve/edit flow).
  async function handleWriteReply(uiKey: string, rowId: string | undefined) {
    const text = (replyText[uiKey] ?? '').trim();
    setReplyErr((s) => ({ ...s, [uiKey]: '' }));
    if (!rowId) {
      setReplyErr((s) => ({ ...s, [uiKey]: 'We could not identify this review. Please refresh and try again.' }));
      return;
    }
    if (text.length < 2) {
      setReplyErr((s) => ({ ...s, [uiKey]: 'Write a reply before posting.' }));
      return;
    }
    if (text.length > 4000) {
      setReplyErr((s) => ({ ...s, [uiKey]: 'That reply is too long (max 4000 characters).' }));
      return;
    }
    setReplyBusy(uiKey);
    try {
      await writeReply(rowId, text);
      setReplyText((s) => {
        const n = { ...s };
        delete n[uiKey];
        return n;
      });
      setToast("Reply approved. It's ready to post to your Google review.");
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const code = (e as { message?: string })?.message ?? '';
      let msg = 'We could not post that reply just now. Please try again.';
      if (status === 409 && code === 'draft_exists') {
        msg = 'Rhody Reviews already drafted a reply for this review. Refresh to approve or edit that draft instead.';
      } else if (status === 409 && code === 'already_replied') {
        msg = 'This review has already been replied to. Refresh to see it.';
      } else if (status === 404) {
        msg = 'That review could not be found. It may have been removed.';
      } else if (status === 400 && code === 'text_too_long') {
        msg = 'That reply is too long (max 4000 characters).';
      } else if (status === 400 && code === 'missing_text') {
        msg = 'Write a reply before posting.';
      }
      setReplyErr((s) => ({ ...s, [uiKey]: msg }));
    } finally {
      setReplyBusy(null);
    }
  }
  // FIX 1: open / cancel / submit an edit of an existing reply. Seeds the
  // textarea with the reply currently shown on the review.
  function startEditReply(item: ReviewItem) {
    setEditReplyId(item.id);
    setEditReplyText(item.draft ?? '');
    setEditReplyErr(null);
  }
  function cancelEditReply() {
    setEditReplyId(null);
    setEditReplyText('');
    setEditReplyErr(null);
  }
  async function submitEditReply(item: ReviewItem) {
    const text = editReplyText.trim();
    setEditReplyErr(null);
    if (!item.rowId) {
      setEditReplyErr('We could not identify this review. Please refresh and try again.');
      return;
    }
    if (text.length < 2) {
      setEditReplyErr('Write a reply before saving.');
      return;
    }
    if (text.length > 4000) {
      setEditReplyErr('That reply is too long (max 4000 characters).');
      return;
    }
    setEditReplyBusy(true);
    try {
      await editReply(item.rowId, text);
      cancelEditReply();
      setToast('Reply updated. The new version is ready to post to your Google review.');
    } catch (e) {
      const status = (e as { status?: number })?.status;
      let msg = 'We could not update that reply just now. Please try again.';
      if (status === 404) msg = 'That review could not be found. It may have been removed.';
      else if (status === 400) msg = 'That reply is too long (max 4000 characters).';
      setEditReplyErr(msg);
    } finally {
      setEditReplyBusy(false);
    }
  }
  async function handleApproveAllFiveStar() {
    // U7: gather the 5-star needs-reply reviews that have a pending draft.
    const targets = needsReply
      .filter((i) => i.stars >= 5)
      .map((i) => draftIdByReview.get(i.id))
      .filter((d): d is string => !!d);
    if (targets.length === 0) return; // nothing to do (button is also hidden)

    // Confirm before bulk-approving. Rhody never auto-posts (Hard Rule #1); this
    // approves the drafts and marks them ready for you to post to Google.
    const ok = window.confirm(
      `Approve ${targets.length} five-star ${targets.length === 1 ? 'reply' : 'replies'}?`,
    );
    if (!ok) return;

    let posted = 0;
    let failed = 0;
    await Promise.all(
      targets.map((d) =>
        approve(d).then(
          () => {
            posted += 1;
          },
          () => {
            failed += 1;
          },
        ),
      ),
    );
    // One success toast summarizing the batch (U7). Approve records the reply
    // and marks it ready; it does not push live to Google (manual posting is
    // the v1 default), so the copy says "ready to post," not "posted."
    setToast(
      failed === 0
        ? `Approved ${posted} ${posted === 1 ? 'reply' : 'replies'}. They're ready to post to Google.`
        : `Approved ${posted}; ${failed} could not be saved. Please try those again.`,
    );
  }
  function handleExportCsv() {
    const header = ['Reviewer', 'Stars', 'Source', 'When', 'Review', 'Reply'];
    const esc = (v: string) => '"' + String(v ?? '').replace(/"/g, '""') + '"';
    const rows = visible.map((r) =>
      [r.who, String(r.stars), r.source, r.when, r.text, r.draft ?? ''].map(esc).join(','),
    );
    const csv = [header.map(esc).join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rhody-reviews-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setToast('Downloaded your reviews CSV.');
  }

  function onTab(i: number) {
    setSeg(i);
    syncUrl({ segLabel: TABS[i].label });
  }

  /* --------------------- one review card --------------------- */
  function renderItem(item: ReviewItem) {
    const flagged = isFlagged(item);
    const sent = sentimentOf(item.stars, flagged);
    const replied = isReplied(item);
    const hasDraft = draftIdByReview.has(item.id);
    const editing = editingId === item.id;
    const tone = toneById[item.id] ?? 'warm';
    const draftText = effectiveDraft(item);
    // Needs a reply if not yet replied. Skipped state is transient in the
    // prototype demo; the live inbox refreshes off the query, so a review is
    // either needs-reply or replied here.
    const needs = !replied;

    return (
      <div
        key={item.id}
        className="rv-item"
        style={{ ['--rv-accent' as string]: sent.accent }}
      >
        <div className="rv-item__pad">
          <div className="rv-item__row">
            <div className="rv-ava" style={{ background: item.color }}>
              {item.initial}
            </div>
            <div className="rv-item__main">
              <div className="rv-item__head">
                <span className="rv-name">{item.who}</span>
                <Stars15 n={item.stars} />
                <span
                  className="rv-sent"
                  style={{ background: sent.bg, color: sent.fg }}
                >
                  <span className="rv-sent__dot" style={{ background: sent.accent }} />
                  {sent.label}
                </span>
              </div>
              <div className="rv-posted" title={item.when}>
                {GoogleG}
                Posted on Google &middot; {item.when}
              </div>
              <div className="rv-quote">
                <p>{item.text}</p>
              </div>

              {/* AI-DRAFT flow: the review has a pending AI draft to approve/
                  edit/skip (Hard Rule #1: approve is an explicit human click). */}
              {needs && hasDraft && (
                <div className="rv-draft">
                  <div className="rv-draft__spine">
                    <div className="rv-draft__spine-icon">{ReplyArrow}</div>
                    <div className="rv-draft__spine-line" />
                  </div>
                  <div className="rv-draft__main">
                    <div className="rv-draft__labels">
                      <span className="rv-draft__title">Your reply</span>
                      {/* U5: only the AI's own draft (the 'warm' default) may be
                          labelled "AI DRAFT". The Short / Sincere chips apply a
                          local rewrite, so we relabel to "EDITED" -- never claim
                          non-AI text is AI. */}
                      {tone === 'warm' && !editing ? (
                        <span className="rv-aitag">
                          {SparkGlyph}
                          AI DRAFT &middot; YOUR VOICE
                        </span>
                      ) : (
                        <span className="rv-aitag rv-aitag--edited">Edited</span>
                      )}
                    </div>
                    <div className="rv-draft__box">
                      {editing && !readOnly ? (
                        <textarea
                          className="rv-draft__ta"
                          value={draftText}
                          onChange={(e) =>
                            setDraftById((s) => ({ ...s, [item.id]: e.target.value }))
                          }
                        />
                      ) : (
                        <p>{draftText}</p>
                      )}
                    </div>

                    {!readOnly && (
                      <>
                        <div className="rv-tone">
                          <span className="rv-tone__lab">TONE</span>
                          {(['warm', 'short', 'sincere'] as Tone[]).map((t) => (
                            <button
                              key={t}
                              type="button"
                              className={'rv-tonechip' + (tone === t ? ' is-on' : '')}
                              onClick={() => setTone(item.id, t)}
                            >
                              {t === 'warm' ? 'Warm' : t === 'short' ? 'Short' : 'Sincere'}
                            </button>
                          ))}
                        </div>

                        <div className="rv-actions">
                          <button
                            className="rv-approve"
                            type="button"
                            onClick={() => handleApprove(item)}
                            disabled={actionPending}
                            title="Approve this reply and mark it ready to post to your Google review"
                          >
                            <Icon d={CheckPath} sw={3} /> Approve
                          </button>
                          <button
                            className="rv-edit"
                            type="button"
                            onClick={() => toggleEdit(item)}
                          >
                            {editing ? 'Done' : 'Edit'}
                          </button>
                          <button
                            className="rv-skip"
                            type="button"
                            onClick={() => handleSkip(item.id)}
                            disabled={actionPending}
                          >
                            Skip
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* MANUAL reply flow (U6): the review has NO AI draft, so the
                  owner writes their own reply and posts it. Fires only on the
                  explicit "Post reply" click; never auto-posts. */}
              {needs && !hasDraft && !readOnly && (
                <div className="rv-draft">
                  <div className="rv-draft__spine">
                    <div className="rv-draft__spine-icon">{ReplyArrow}</div>
                    <div className="rv-draft__spine-line" />
                  </div>
                  <div className="rv-draft__main">
                    <div className="rv-draft__labels">
                      <span className="rv-draft__title">Write a reply</span>
                    </div>
                    <div className="rv-draft__box">
                      <textarea
                        className="rv-draft__ta"
                        placeholder="Write a reply to post on Google..."
                        value={replyText[item.id] ?? ''}
                        maxLength={4000}
                        onChange={(e) =>
                          setReplyText((s) => ({ ...s, [item.id]: e.target.value }))
                        }
                      />
                    </div>
                    {replyErr[item.id] && (
                      <div className="rv-reply-err">{replyErr[item.id]}</div>
                    )}
                    <div className="rv-actions">
                      <button
                        className="rv-approve"
                        type="button"
                        onClick={() => void handleWriteReply(item.id, item.rowId)}
                        disabled={replyBusy === item.id || !(replyText[item.id] ?? '').trim()}
                        title="Approve this reply and mark it ready to post to your Google review"
                      >
                        <Icon d={CheckPath} sw={3} />{' '}
                        {replyBusy === item.id ? 'Approving...' : 'Approve reply'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Read-only operator view of a draftless, unreplied review. */}
              {needs && !hasDraft && readOnly && (
                <div className="rv-draft">
                  <div className="rv-draft__main">
                    <p className="rv-reply-note">No reply yet.</p>
                  </div>
                </div>
              )}

              {replied && (
                <div className="rv-replied">
                  <div className="rv-replied__icon">
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#188038"
                      strokeWidth="2.5"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <div className="rv-replied__main">
                    <div className="rv-replied__labels">
                      <span className="rv-draft__title">Your reply</span>
                      <span className="rv-posted-pill">
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#188038"
                          strokeWidth="3"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        REPLY APPROVED
                      </span>
                    </div>
                    {editReplyId === item.id && !readOnly ? (
                      <>
                        <div className="rv-replied__box">
                          <textarea
                            className="rv-draft__ta"
                            value={editReplyText}
                            maxLength={4000}
                            onChange={(e) => setEditReplyText(e.target.value)}
                          />
                        </div>
                        {editReplyErr && <div className="rv-reply-err">{editReplyErr}</div>}
                        <div className="rv-actions">
                          <button
                            className="rv-approve"
                            type="button"
                            onClick={() => void submitEditReply(item)}
                            disabled={editReplyBusy || !editReplyText.trim()}
                            title="Save this edited reply and mark it ready to post to your Google review"
                          >
                            <Icon d={CheckPath} sw={3} />{' '}
                            {editReplyBusy ? 'Saving...' : 'Update reply'}
                          </button>
                          <button
                            className="rv-edit"
                            type="button"
                            onClick={cancelEditReply}
                            disabled={editReplyBusy}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rv-replied__box">
                          <p>{item.draft}</p>
                        </div>
                        {!readOnly && (
                          <div className="rv-actions">
                            <button
                              className="rv-edit"
                              type="button"
                              onClick={() => startEditReply(item)}
                            >
                              Edit reply
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rv">
      {/* desktop / tablet action bar. V7: the page TITLE + sub live in the
          AppShell topbar already, so this row no longer repeats them -- it
          carries only the reviews-specific search + Request-reviews action
          (right-aligned), collapsing the former doubled header into one. */}
      <div className="rv-head rv-head--actions">
        <div className="rv-head__right">
          <div className="rv-search">
            <Icon d={SearchPath} />
            <input
              placeholder="Search reviews..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                syncUrl({ q: e.target.value });
              }}
            />
          </div>
          {!readOnly && (
            <button
              className="rv-cta"
              type="button"
              title="Request reviews"
              onClick={() => navigate('/requests')}
            >
              <Icon d={PlusPath} sw={2.5} /> Request reviews
            </button>
          )}
        </div>
      </div>

      {/* mobile-only compact header */}
      <div className="rv-mhead">
        <div className="rv-mhead__title">Reviews</div>
        <div className="rv-mhead__sub">
          <strong>{needsCount}</strong> waiting &middot; nothing posts without you
        </div>
      </div>

      <div className="rv-body">
        {/* tabs */}
        <div className="rv-tabs">
          <div className="rv-tabset">
            {TABS.map((t, i) => (
              <button
                key={t.label}
                type="button"
                className={'rv-tab' + (i === seg ? ' is-active' : '')}
                onClick={() => onTab(i)}
              >
                {t.label} &middot; {t.badge}
              </button>
            ))}
          </div>
        </div>

        {/* mobile-only bulk approve (desktop has it in the rail). U7: hidden
            when there is nothing to approve. */}
        {!readOnly && fiveStarNeeds > 0 && (
          <button className="rv-mbulk" type="button" onClick={handleApproveAllFiveStar}>
            <Icon d={CheckPath} sw={3} /> Approve all {fiveStarNeeds} five-star drafts
          </button>
        )}

        {actionErr && (
          <div
            className="rv-action-err"
            role="alert"
            style={{
              margin: '0 0 12px',
              padding: '10px 14px',
              borderRadius: 10,
              background: '#fce8e6',
              color: '#c5221f',
              fontSize: 13.5,
              fontWeight: 600,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <span>{actionErr}</span>
            <button
              type="button"
              onClick={() => setActionErr(null)}
              style={{ background: 'none', border: 'none', color: '#c5221f', cursor: 'pointer', fontWeight: 700 }}
              aria-label="Dismiss"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="rv-grid">
          {/* LIST */}
          <div className="rv-list">
            {isLoading && <div className="rv-state">Loading reviews...</div>}
            {isError && !isLoading && (
              <div className="rv-state">
                {friendlyError(error?.message, 'Could not load your reviews. Please try again.')}
                {error?.message && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12, opacity: 0.7 }}>Details</summary>
                    <code style={{ fontSize: 11 }}>{error.message}</code>
                  </details>
                )}
              </div>
            )}
            {!isLoading && !isError && visible.length === 0 && (
              <div className="rv-empty">
                <div className="rv-empty__glyph">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#34a853"
                    strokeWidth="2.5"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <div className="rv-empty__title">Nothing here</div>
                <div className="rv-empty__desc">No reviews match this filter.</div>
                <button className="rv-empty__btn" type="button" onClick={() => onTab(3)}>
                  Show all reviews
                </button>
              </div>
            )}
            {!isLoading && !isError && visible.map((item) => renderItem(item))}
          </div>

          {/* RIGHT RAIL (desktop + enhanced-mobile) */}
          <div className="rv-rail">
            <div className="rv-railcard">
              <div className="rv-railcard__title">Clear your queue</div>
              <div className="rv-railcard__desc">
                <strong>{needsCount}</strong> reviews are waiting. <strong>{fiveStarNeeds}</strong>{' '}
                are 5-star and safe to approve in one tap.
              </div>
              {!readOnly && fiveStarNeeds > 0 && (
                <button
                  className="rv-bulk"
                  type="button"
                  onClick={handleApproveAllFiveStar}
                  disabled={actionPending}
                >
                  <Icon d={CheckPath} sw={3} /> Approve all {fiveStarNeeds} five-star drafts
                </button>
              )}
              <div className="rv-note">
                {/* Stroke deepened from #ea8600 to #8a4600 (Brad, 2026-08-06):
                    2.59:1 was under the 3:1 WCAG 1.4.11 non-text minimum. */}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8a4600"
                  strokeWidth="2"
                  style={{ flexShrink: 0, marginTop: 1 }}
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v.01M11 12h1v4h1" />
                </svg>
                <span>
                  Low-star reviews stay manual. They deserve a personal reply, never a bulk one.
                </span>
              </div>
              <button className="rv-export" type="button" onClick={handleExportCsv}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
                </svg>
                Export to CSV
              </button>
            </div>

            <div className="rv-railcard">
              <div className="rv-railcard__title" style={{ marginBottom: 14 }}>
                Your queue at a glance
              </div>
              <div className="rv-glance__row">
                <span className="rv-glance__l">
                  <span className="rv-glance__dot" style={{ background: 'var(--rr-blue)' }} />
                  Needs reply
                </span>
                <span className="rv-glance__n">{needsCount}</span>
              </div>
              <div className="rv-glance__row">
                <span className="rv-glance__l">
                  <span className="rv-glance__dot" style={{ background: 'var(--rr-green)' }} />
                  Replied
                </span>
                <span className="rv-glance__n">{repliedCount}</span>
              </div>
              <div className="rv-glance__row">
                <span className="rv-glance__l">
                  <span className="rv-glance__dot" style={{ background: 'var(--rr-red)' }} />
                  Flagged for care
                </span>
                <span className="rv-glance__n">{flaggedCount}</span>
              </div>
            </div>

            <div className="rv-trust">
              <div className="rv-trust__row">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1a73e8"
                  strokeWidth="2"
                  style={{ flexShrink: 0, marginTop: 1 }}
                >
                  <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <div>
                  <div className="rv-trust__t">You approve every reply</div>
                  <div className="rv-trust__d">
                    Rhody never auto-posts. AI drafts in your voice; nothing reaches Google
                    until you tap approve.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transient toast (U7 bulk-approve summary, U9 export feedback). */}
      {toast && (
        <div className="rv-toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
}
